import PropTypes from 'prop-types';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {GROWTH_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants, {
  COPYABLE_FOLDER_ITEMS,
  SHARE_LINK_FOLDER_ITEMS,
} from 'constants/ModelTypeConstants';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as ModalContainer from 'ui-library/ModalContainer';

import ShareLinkModal, {SHARE_LINK_MODAL_KEY} from './growth/ShareLinkCreationModal';

interface Props {
  fileId: string;
  fileName: string;
  fileType: ModelTypeConstants;
  onDuplicate?: () => void;
  filePerms?: Permissions;
}

function CopyButton({fileId, onDuplicate}: {fileId: string; onDuplicate: () => void}): JSX.Element {
  return (
    <Tooltip container="body" placement={TooltipPlacement.TOP} title="Copy to...">
      <Button
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="copy"
        onClick={onDuplicate}
        size={ButtonSize.MINI}
        disabled={!isFeatureEnabled({feature: StandardFeature.CREATE, item: {id: fileId}})}
      />
    </Tooltip>
  );
}

function ShareBar(props: Props): JSX.Element {
  const {fileId, fileName, fileType, onDuplicate, filePerms} = props;
  const userId = EntityStore.currentUser().id;
  const canShare = BenchlingConfig.ENABLE_SHARE_LINK && SHARE_LINK_FOLDER_ITEMS.includes(fileType);
  const canCopy = COPYABLE_FOLDER_ITEMS.includes(fileType) && filePerms?.readable && onDuplicate;

  return (
    <div className="buffer-tabs-shareBar" {...getTestProps({component: 'ShareBar', element: 'root'})}>
      {canShare && (
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          icon="share-v2"
          className="u-mini-spacing-right"
          size={ButtonSize.MINI}
          text="Share"
          onClick={() => {
            analytics.trackEvent(GROWTH_ANALYTICS_ACTIONS.OPENED_SHARE_LINK_CREATION_MODAL, {
              file_id: fileId,
              user_id: userId,
            });
            ModalContainer.open(
              SHARE_LINK_MODAL_KEY,
              <ShareLinkModal fileId={fileId} fileName={fileName} userId={userId} />
            );
          }}
          dataTestAttrs={{component: 'ShareBar', element: 'shareButton'}}
        />
      )}
      {canCopy && <CopyButton fileId={fileId} onDuplicate={onDuplicate} />}
    </div>
  );
}

ShareBar.propTypes = {
  fileId: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  onDuplicate: PropTypes.func,
};

export default ShareBar;
